import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { GatsbyImage } from 'gatsby-plugin-image';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';

import styled from '@emotion/styled';
import { removeNoTranslateWrapper, replaceWithComponent } from '../utilities/misc';

import HubspotForm from './HubspotForm';
import TrialFormLoader from './trialForm/TrialFormLoader';
import Link from './Link';
import Table from './table/Table';
import Personalization from './Personalization';
import PhoneSalesSupport from './PhoneSalesSupport';
import VariableText from './VariableText';
import VideoPlayer from './VideoPlayer';
import TableCell from './table/TableCell';
import TableRow from './table/TableRow';
import TableHeaderCell from './table/TableHeaderCell';
import AnchorElement from './AnchorElement';

// There won't be a circular reference because widgetCaption does not allow embedded entries
// eslint-disable-next-line import/no-cycle
import WidgetItem from './WidgetItem';

// eslint-disable-next-line import/no-cycle, import/newline-after-import
import RichTextImage from './RichTextImage';
import Button from './buttons/Button';
import ButtonText from './buttons/ButtonText';
import ButtonBlock from './ButtonBlock';
import CalloutCard from './CalloutCard'; // eslint-disable-line import/no-cycle
import PdfEmbed from './PdfEmbed';
import { rem } from '../utilities/style';
import { media } from '../theme/media';

const priceVariableRe = /{{price-([a-z0-9-_.]+)}}/gi;
const linkVariableRe = /{{link-([a-z0-9-_.]+)}}/gi;
const textVariableRe = /{{text-([a-z0-9-_.]+)}}/gi;
const phoneVariableRe = /{{phone-([a-z0-9-_.]+)}}/gi;
const formVariableRe = /{{form-([a-z0-9-_.]+)}}/i;

const DynamicPrice = ({ value }) => (
  <VariableText type="price" name={value} />
);

const DynamicLink = ({ value }) => (
  <VariableText type="link" name={value} />
);

const DynamicText = ({ value }) => (
  <VariableText type="text" name={value} />
);

const Video = styled.div`
  margin-top: ${rem(32)};

  ${media('>tiny')} {
    width: 100%;
  }

  ${media('>small')} {
    margin: ${rem(32)} auto 0;
  }
`;

const DynamicPhoneNumber = ({ value }) => (
  <VariableText
    type="phone-number"
    name={value}
    render={(phoneNumbers) => {
      // Directly handle rendering logic based on the value
      switch (value) {
        case 'sales-only':
          return <PhoneSalesSupport salesPhone={phoneNumbers?.salesPhone} />;
        case 'support-only':
          return <PhoneSalesSupport supportPhone={phoneNumbers?.supportPhone} />;
        default:
          // Assuming 'sales-support' or any other value should show both
          return (
            <PhoneSalesSupport
              salesPhone={phoneNumbers?.salesPhone}
              supportPhone={phoneNumbers?.supportPhone}
            />
          );
      }
    }}
  />
);

const Newline = () => <br />;

const replaceAllTags = (text) => {
  let textArray = [];

  textArray = replaceWithComponent(/(\n)/g, text, Newline, 'nl');
  textArray = replaceWithComponent(priceVariableRe, textArray, DynamicPrice, 'price');
  textArray = replaceWithComponent(linkVariableRe, textArray, DynamicLink, 'link');
  textArray = replaceWithComponent(textVariableRe, textArray, DynamicText, 'text');
  textArray = replaceWithComponent(
    phoneVariableRe,
    textArray,
    DynamicPhoneNumber,
    'phone'
  );

  textArray = textArray.map(removeNoTranslateWrapper);

  return textArray;
};

const richTextRenderOptions = {
  renderText: replaceAllTags,
  renderNode: {
    [BLOCKS.HEADING_2]: ({ data }, children) => (
      <h2>
        {data?.id !== undefined && (
          <AnchorElement id={data?.id} />
        )}
        {children}
      </h2>
    ),
    [BLOCKS.TABLE]: (node, children) => <Table>{children}</Table>,
    [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
      <TableHeaderCell>{children}</TableHeaderCell>
    ),
    [BLOCKS.TABLE_ROW]: (node, children) => <TableRow>{children}</TableRow>,
    [BLOCKS.TABLE_CELL]: (node, children) => <TableCell>{children}</TableCell>,
    [BLOCKS.EMBEDDED_ASSET]: ({ data }) => {
      const { target } = data;

      if (target === undefined) {
        return null;
      }

      if (target.file.contentType === 'application/pdf' && target.file.url) {
        return (
          <PdfEmbed
            url={target.file.url}
            description={target.description}
          />
        );
      }

      if (target.gatsbyImageData) {
        return (
          <GatsbyImage image={target.gatsbyImageData} alt={target.description} />
        );
      }

      return null;
    },
    [BLOCKS.EMBEDDED_ENTRY]: ({ data }) => {
      const { target } = data;
      let formVariableName = null;

      if (target === undefined) {
        return null;
      }

      if (target.__typename === 'ContentfulForm') {
        const formVariableMatch = target.hubSpotFormId
          ? target.hubSpotFormId.match(formVariableRe) : null;
        formVariableName = formVariableMatch ? formVariableMatch[1] : null;
      }

      return (
        <Personalization options={target.personalizations}>
          {target.__typename === 'ContentfulForm' && (
            <VariableText
              type="form"
              name={formVariableName}
              render={(formId) => (
                (formId || target.hubSpotFormId) && (
                  <HubspotForm
                    formId={formVariableName ? formId : target.hubSpotFormId}
                    sfdcCampaignId={target.salesforceCampaignId}
                    redirectUrl={target.redirectUrl}
                    hubspotFieldOverrides={target.hubspotFieldOverrides}
                    formOverrides={target.formOverrides}
                    copyField={target.copyField}
                  />
                )
              )}
            />
          )}
          {target.__typename === 'ContentfulTrialForm' && (
            <TrialFormLoader
              variation={target.variation}
            />
          )}
          {target.__typename === 'ContentfulVideo' && (
            <Video>
              <VideoPlayer
                url={target.videoUrl}
                title={target.title}
                description={target.videoDescription?.description}
                thumbnailUrl={target.thumbnail?.file?.url}
                thumbnailImage={target.thumbnail?.gatsbyImageData}
                videoBorder={target?.videoBorder}
                uploadDate={target.createdAt}
              />
            </Video>
          )}
          {target.__typename === 'ContentfulImage' && (
            <RichTextImage
              image={target.image}
              imageLink={target.imageLink}
              alignment={target.alignment}
              width={target.width}
              imageCaption={target.imageCaption}
            />
          )}
          {target.__typename === 'ContentfulWidget' && (
            <WidgetItem
              widgetTitle={target.widgetTitle}
              widgetType={target.widgetType}
              widgetId={target.widgetId}
              widgetCaption={target.widgetCaption}
            />
          )}
          {target.__typename === 'ContentfulButton' && (
            <ButtonBlock>
              {!target.style || target.style === 'Primary' ? (
                <Button
                  to={target.url}
                  targetBlank={target.targetBlank}
                >
                  {replaceAllTags(target.title)}
                </Button>
              ) : (
                <ButtonText
                  to={target.url}
                  targetBlank={target.targetBlank}
                >
                  {replaceAllTags(target.title)}
                </ButtonText>
              )}
            </ButtonBlock>
          )}
          {target.__typename === 'ContentfulCalloutCard' && (
            <CalloutCard
              title={target.title}
              description={target.description}
              ctaText={target.ctaText}
              to={target.ctaLink}
              targetBlank={target.targetBlank}
              layout="large"
            />
          )}
        </Personalization>
      );
    },
    [INLINES.ASSET_HYPERLINK]: ({ data }, children) => {
      const { target } = data;

      return <Link to={target.file.url}>{children}</Link>;
    },
    [INLINES.ENTRY_HYPERLINK]: ({ data }, children) => {
      const { target } = data;

      return (
        <Personalization options={target.personalizations}>
          {target.__typename === 'ContentfulMedia' && (
            <a href={target.media?.file?.url}>{children}</a>
          )}
        </Personalization>
      );
    },
    [INLINES.HYPERLINK]: ({ data }, children) => (
      <Link to={data.uri}>
        {children}
      </Link>
    ),
  },
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    highlight: (text) => <mark>{text}</mark>
  }
};

const TextRenderer = ({ text, richText = false }) => {
  if (text === null || text === undefined) {
    return null;
  }

  if (richText) {
    return renderRichText(text, richTextRenderOptions);
  }

  return replaceAllTags(text);
};

TextRenderer.propTypes = {
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      raw: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      references: PropTypes.arrayOf(PropTypes.object)
    })
  ]),
  richText: PropTypes.bool
};

export const query = graphql`
  fragment TextRendererAssetFragment on ContentfulAsset {
    __typename
    contentful_id
    gatsbyImageData(quality: 75)
    description
    file {
      contentType
      url
    }
  }
  fragment TextRendererButtonFragment on ContentfulButton {
    __typename
    contentful_id
    title
    url
    targetBlank
    style
    personalizations {
      ...PersonalizationFragment
    }
  }
  fragment TextRendererCalloutCardFragment on ContentfulCalloutCard {
    __typename
    contentful_id
    title
    description
    ctaLink
    ctaText
    targetBlank
    personalizations {
      ...PersonalizationFragment
    }
  }
  fragment TextRendererMediaFragment on ContentfulMedia {
    __typename
    contentful_id
    title
    media {
      file {
        url
      }
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
  fragment TextRendererFormFragment on ContentfulForm {
    __typename
    contentful_id
    hubSpotFormId
    salesforceCampaignId
    redirectUrl
    hubspotFieldOverrides
    copyField
    formOverrides
    personalizations {
      ...PersonalizationFragment
    }
  }
  fragment TextRendererTrialFormFragment on ContentfulTrialForm {
    __typename
    contentful_id
    variation
    personalizations {
      ...PersonalizationFragment
    }
  }
  fragment TextRendererImageFragment on ContentfulImage {
    __typename
    contentful_id
    image {
      gatsbyImageData(quality: 75)
      description
      file {
        url
        details {
          image {
            height
            width
          }
        }
      }
    }
    imageLink
    alignment
    width
    imageCaption {
      raw
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
  fragment TextRendererVideoFragment on ContentfulVideo {
    __typename
    contentful_id
    createdAt(formatString: "YYYY-MM-DD")
    title
    videoUrl
    videoBorder
    videoDescription: description {
      description
    }
    thumbnail {
      gatsbyImageData(width: 1280, height: 720, quality: 75, resizingBehavior: FILL)
      file {
        url
      }
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
  fragment TextRendererWidgetFragment on ContentfulWidget {
    __typename
    contentful_id
    widgetTitle
    widgetType
    widgetId
    widgetCaption{
      raw
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default TextRenderer;
